import React, { Suspense, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { useDispatch } from 'store/configureStore';

import {
  deleteCoupon,
  deleteRedeemCashback,
} from 'actions/entities/orders/orders';
import { useCalculateRedeemCashback } from 'hooks/useRedeemCashback/useCalculateRedeemCashback';
import {
  getCouponCode,
  getOrder,
  isCouponApplied,
  isLoggedIn,
  isPayingFromChina,
  isPayingFromIndia,
  isRedeemCashbackApplied,
  shouldShowCoupon,
  shouldShowRedeemCashback,
} from 'selectors';

import { Spinner } from 'components/Spinner/Spinner';
import { Coupon } from '../Coupon/Coupon';

const RedeemCashback = React.lazy(() =>
  import('../RedeemCashback/RedeemCashback').then(module => ({
    default: module.RedeemCashback,
  })),
);

const Discounts = ({ className }: { className?: string }) => {
  const dispatch = useDispatch();
  const { id: orderId, token } = useSelector(getOrder);
  const payingFromChina = useSelector(isPayingFromChina);
  const payingFromIndia = useSelector(isPayingFromIndia);
  const showCoupon = useSelector(shouldShowCoupon);
  const couponApplied = useSelector(isCouponApplied);
  const showRedeemCashback = useSelector(shouldShowRedeemCashback);
  const redeemCashbackApplied = useSelector(isRedeemCashbackApplied);
  const couponCode = useSelector(getCouponCode);
  const loggedIn = useSelector(isLoggedIn);

  const { data: calculateRedeemCashback } = useCalculateRedeemCashback(
    {
      orderId,
      token,
    },
    { enabled: showRedeemCashback },
  );

  const {
    availableRedeemAmount = 0,
    walletAmount = 0,
    bonusAmount,
  } = calculateRedeemCashback ?? {};

  const isRedeemCashbackEnabled =
    showRedeemCashback && availableRedeemAmount > 0;

  const isCouponsEnabled =
    (payingFromChina || payingFromIndia) &&
    showCoupon &&
    !couponApplied &&
    !isRedeemCashbackEnabled;

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (couponApplied) {
      dispatch(deleteCoupon(couponCode as string));
    }

    if (redeemCashbackApplied) {
      dispatch(deleteRedeemCashback());
    }
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (isRedeemCashbackEnabled && couponApplied) {
      dispatch(deleteCoupon(couponCode as string));
    }
  }, [couponApplied, isRedeemCashbackEnabled]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (!loggedIn && redeemCashbackApplied) {
      dispatch(deleteRedeemCashback());
    }
  }, [loggedIn]);

  return (
    <div data-testid="Discounts" className={className}>
      {isCouponsEnabled && <Coupon />}
      {isRedeemCashbackEnabled && (
        <Suspense fallback={<Spinner />}>
          <RedeemCashback
            availableRedeemAmount={availableRedeemAmount}
            walletAmount={walletAmount}
            bonusAmount={bonusAmount}
          />
        </Suspense>
      )}
    </div>
  );
};

export { Discounts };
